import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { get, isNil, trim } from 'lodash';
import { BehaviorSubject, throwError } from 'rxjs';

import { ScenarioPlannerService } from '@app/shared/services/api/scenario-planner.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { catchError, first } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-save-scenario-dialog',
  templateUrl: './save-scenario-dialog.component.html',
  styleUrls: ['./save-scenario-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SaveScenarioDialogComponent implements OnInit {
  name = '';
  isSaved = false;
  isDuplicate: boolean = false;
  isLoading$ = new BehaviorSubject(false);
  hideClose = true;
  model: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private matDialog: MatDialog,
    private matDialogRef: MatDialogRef<SaveScenarioDialogComponent>,
    private scenarioPlannerService: ScenarioPlannerService
  ) {
    this.matDialogRef.disableClose = false;
  }

  public ngOnInit(): void {
    this.model = this.data;
    if (!isNil(get(this.model, 'plannerScenarioId', null))) {
      this.update();
    }
  }

  private update(): void {
    this.matDialogRef.disableClose = true;
    this.isLoading$.next(true);
    this.scenarioPlannerService
      .put(this.model.plannerScenarioId, this.model)
      .pipe(first())
      .subscribe(() => {
        this.isSaved = true;

        this.hideModal(3000, {
          plannerScenarioId: this.model.plannerScenarioId,
          name: this.model.name,
        });

        this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.streetlightPlannerUpdate, {
          scenarioId: this.model.plannerScenarioId,
          scenarioName: this.name,
          scenarioTypeId: this.model.scenarioTypeId,
          scenarioType: this.model.scenarioType,
          budget: this.model.budget,
          municipalityId: this.model.municipalityEntityId,
          municipalityName: this.model.municipalityName,
        });
        this.isLoading$.next(false);
      });
  }

  public save(): void {
    if (this.isValid()) {
      this.matDialogRef.disableClose = true;
      this.isLoading$.next(true);
      this.isDuplicate = false;
      this.scenarioPlannerService
        .post(Object.assign(this.model, { name: this.name }))
        .pipe(
          catchError((err: HttpErrorResponse) => {
            if (err.status === 400) {
              this.isDuplicate = true;
              this.isLoading$.next(false);
            }
            return throwError(() => err);
          }),
          first()
        )
        .subscribe((id: string) => {
          this.isLoading$.next(false);
          this.isSaved = true;
          this.hideModal(3000, {
            plannerScenarioId: id,
            name: this.name,
          });

          this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.streetlightPlannerSave, {
            scenarioId: id,
            scenarioName: this.name,
            scenarioTypeId: this.model.scenarioTypeId,
            scenarioType: this.model.scenarioType,
            budget: this.model.budget,
            municipalityId: this.model.municipalityGuid,
            municipalityName: this.model.municipalityName,
          });
        });
    }
  }

  public closeDuplicateMessage(): void {
    this.isDuplicate = false;
    this.isLoading$.next(false);
    this.isSaved = false;
  }

  private hideModal(time: number, data: any): void {
    setTimeout(() => {
      this.matDialog.closeAll();
      this.matDialogRef.close(data);
    }, time);
  }

  public isValid(): boolean {
    return !isNil(this.name) && trim(this.name) !== '';
  }

  public close(): void {
    this.matDialogRef.close();
  }
}
